import React from "react";
import { useHistory } from "react-router-dom";
import Alert from "@material-ui/lab/Alert";
import CameraIcon from "@material-ui/icons/Videocam";

import { LocalizedText } from "../LocalizedText";
import { Item, ItemList } from "../ItemList";
import { AddButton, DeleteButton, Panel, PanelHeader } from "../Panel";

import { CameraListViewProps } from "./interfaces";
import { dataToItems } from "./transformers";
import { useTranslation } from "../../hooks/i18n";

export const CameraListView: React.FC<CameraListViewProps> = ({
  data,
  loading,
  error,
  accountId,
  onDelete,
  deleteSuccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [checkedItems, setCheckedItems] = React.useState<Item[]>([]);

  /**
   * Convert response to <ItemList /> shape
   */
  const dataAsItems = data ? dataToItems(data) : [];

  /**
   * Navigate to specific item when selected
   */
  const handleItemClick = (item: Item) => {
    history.push(`/accounts/summary/${accountId}/cameras/edit/${item.id}`);
  };

  /**
   * Add or remove an item from state when checked/unchecked
   */
  const handleItemCheck = (item: Item, checked: boolean) => {
    setCheckedItems((checkedItems) => {
      const newCheckedItems = [...checkedItems];
      if (checked) {
        newCheckedItems.push(item);
      } else {
        newCheckedItems.splice(checkedItems.indexOf(item), 1);
      }
      return newCheckedItems;
    });
  };

  /**
   * Handle delete button click
   */
  const handleDelete = async () => {
    if (!window.confirm(t("confirm_delete_items"))) return;
    onDelete(checkedItems);
  };

  /**
   * Navigate to create camera when create button pressed
   */
  const handleCreate = () => {
    history.push(`/accounts/summary/${accountId}/cameras/create`);
  };

  const header = (
    <PanelHeader icon={<CameraIcon />} title={t("cameras")}>
      {Boolean(checkedItems.length) && <DeleteButton onClick={handleDelete} />}

      <AddButton label="create_camera" onClick={handleCreate} />
    </PanelHeader>
  );

  return (
    <Panel header={header}>
      {deleteSuccess && (
        <Alert severity="info">
          <LocalizedText text="delete_success" />
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          <LocalizedText text="an_error_occurred" />
        </Alert>
      )}
      {!error && !loading && !dataAsItems.length && (
        <Alert severity="info">
          <LocalizedText text="no_cameras" />
        </Alert>
      )}

      <div aria-label={loading ? t("loading") : undefined}>
        <ItemList
          items={dataAsItems}
          loading={loading}
          onItemClick={handleItemClick}
          onItemCheck={handleItemCheck}
          checkboxes
        />
      </div>
    </Panel>
  );
};
